/**
 * vomFASS CH — Automatic free gift with purchase (Dawn port).
 *
 * Shopify "Buy X get Y" automatic discounts do NOT add product Y to the cart on
 * their own — the customer only gets it free if it is already in the cart. This
 * module guarantees that presence: when the qualifying subtotal reaches a tier's
 * threshold it adds the gift line (tagged with a `_gift` line-item property), and
 * removes it again when the cart drops below the threshold. The Shopify discount
 * is the source of truth for making the line free at checkout.
 *
 * Customers may decline the gift: the cart's remove button on a gift line carries
 * a `data-vf-gift-dismiss` attribute (see sections/main-cart-items.liquid and
 * snippets/cart-drawer.liquid). A click on it records the tier as dismissed in
 * sessionStorage, and dismissed tiers are never (re-)added for the rest of the
 * browser session.
 *
 * Configuration is rendered server-side into a `#vf-auto-gift-config` JSON tag by
 * `snippets/vf-auto-gift.liquid` (driven by `vf_gift_tier` metaobjects), e.g.:
 *
 *   {
 *     "mode": "all",                // "all" = every qualifying tier, "highest" = top tier only
 *     "tiers": [
 *       { "id": "tier-0", "threshold": 6500, "variantId": 123, "handle": "...", "label": "Gratis Geschenk", "fence": [] }
 *     ]
 *   }
 *
 * `threshold` is in the store's minor currency unit (Rappen for CHF), matching
 * cart line prices. `fence` is an optional list of product handles: when non-empty
 * the tier qualifies purely on one of those products being in the cart (the
 * subtotal threshold is then ignored) — used e.g. to tie a gift to the
 * Adventskalender. The whole module is best-effort: any failure leaves the cart
 * untouched rather than blocking the customer.
 */

const VF_AUTO_GIFT_CONFIG_ELEMENT_ID = 'vf-auto-gift-config';
/** Identifies cart updates this module triggered, so it ignores its own events. */
const VF_AUTO_GIFT_EVENT_SOURCE = 'vf-auto-gift';
/** Line-item property key marking a line as an auto-added gift. */
const VF_AUTO_GIFT_PROPERTY = '_gift';
/** sessionStorage key holding the tier ids the customer has dismissed. */
const VF_AUTO_GIFT_DISMISSED_STORAGE_KEY = 'vf-auto-gift:dismissed';

class VfAutoGift {
  /** @type {{ mode: string, tiers: Array<{ id: string, threshold: number, variantId?: number, handle?: string, label?: string, fence?: string[] }> } | null} */
  #config = null;

  /** Guards against re-entrant reconciliation (our own add/remove re-fires the cart update). */
  #busy = false;

  /** Set when a cart update arrives mid-reconciliation, so we settle once more afterwards. */
  #pending = false;

  constructor() {
    this.#config = this.#readConfig();
    if (!this.#config || !Array.isArray(this.#config.tiers) || this.#config.tiers.length === 0) return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, this.#onCartUpdate);

    // Capture phase so the dismissal is recorded before `cart-remove-button`
    // processes the same click and fires the resulting cart update.
    document.addEventListener('click', this.#onDismissClick, true);

    // Reconcile once on load so a returning cart that already qualifies gets its
    // gift (or sheds an orphaned one) without waiting for a fresh cart action.
    this.#reconcile();
  }

  /**
   * @returns {{ mode: string, tiers: Array<object> } | null}
   */
  #readConfig() {
    const element = document.getElementById(VF_AUTO_GIFT_CONFIG_ELEMENT_ID);
    if (!element) return null;
    try {
      return JSON.parse(element.textContent || '');
    } catch {
      return null;
    }
  }

  /**
   * @param {Event} event
   */
  #onDismissClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-vf-gift-dismiss]') : null;
    const tierId = target?.getAttribute('data-vf-gift-dismiss');
    if (tierId) this.#dismissTier(tierId);
  };

  /**
   * @returns {Set<string>} Tier ids the customer has declined this session.
   */
  #dismissedTiers() {
    try {
      const raw = sessionStorage.getItem(VF_AUTO_GIFT_DISMISSED_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch {
      return new Set();
    }
  }

  /**
   * @param {string} tierId
   */
  #dismissTier(tierId) {
    try {
      const dismissed = this.#dismissedTiers();
      dismissed.add(String(tierId));
      sessionStorage.setItem(VF_AUTO_GIFT_DISMISSED_STORAGE_KEY, JSON.stringify([...dismissed]));
    } catch {
      // Storage unavailable (e.g. blocked): worst case the gift is re-added.
    }
  }

  /**
   * @param {{ source?: string }} event
   */
  #onCartUpdate = (event) => {
    // Ignore the cart update we publish ourselves to avoid an infinite loop.
    if (event?.source === VF_AUTO_GIFT_EVENT_SOURCE) return;
    this.#reconcile();
  };

  /**
   * Brings the cart's gift lines in sync with the qualifying subtotal.
   */
  async #reconcile() {
    if (this.#busy) {
      this.#pending = true;
      return;
    }
    this.#busy = true;

    try {
      const cart = await this.#getCart();
      if (!cart || !Array.isArray(cart.items)) return;

      const giftLines = cart.items.filter((item) => this.#giftTierId(item) != null);
      const nonGiftLines = cart.items.filter((item) => this.#giftTierId(item) == null);
      const subtotal = nonGiftLines.reduce((sum, item) => sum + (item.original_line_price ?? item.line_price ?? 0), 0);
      // Product handles present in the cart (gifts excluded), for fence matching.
      const presentHandles = new Set(
        nonGiftLines.map((item) => String(item.handle || '').toLowerCase()).filter(Boolean)
      );

      // Tiers the customer explicitly removed are no longer desired: they are
      // not re-added, and a still-present line (e.g. removal request failed) is
      // cleaned up by the removal pass below.
      const dismissed = this.#dismissedTiers();
      const desiredTiers = this.#desiredTiers(subtotal, presentHandles).filter(
        (tier) => !dismissed.has(String(tier.id))
      );
      const desiredIds = new Set(desiredTiers.map((tier) => tier.id));

      let changed = false;

      // Remove gift lines that no longer qualify, target the wrong variant, or
      // reference a tier that no longer exists in the config.
      for (const line of giftLines) {
        const tierId = this.#giftTierId(line);
        const tier = this.#config?.tiers.find((candidate) => candidate.id === tierId);
        const variantMismatch = tier?.variantId != null && line.variant_id !== tier.variantId;

        if (!tier || !desiredIds.has(tierId) || variantMismatch) {
          if (await this.#removeLine(line.key)) changed = true;
        }
      }

      // Add gift lines that should be present but are missing.
      for (const tier of desiredTiers) {
        const alreadyPresent = giftLines.some(
          (line) =>
            this.#giftTierId(line) === tier.id && (tier.variantId == null || line.variant_id === tier.variantId)
        );
        if (alreadyPresent) continue;

        const variantId = tier.variantId ?? (await this.#resolveVariant(tier.handle));
        if (variantId && (await this.#addGift(variantId, tier))) changed = true;
      }

      if (changed) {
        const fresh = await this.#getCart();
        if (fresh) {
          // Dawn's `cart-items` / `cart-drawer-items` re-render themselves on any
          // cart update that did not originate from them; the header bubble is
          // not part of that, so it is refreshed separately.
          await this.#renderCartIconBubble();
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: VF_AUTO_GIFT_EVENT_SOURCE,
            cartData: fresh,
          });
        }
      }
    } catch {
      // Best-effort: never let a gift hiccup interfere with the cart.
    } finally {
      this.#busy = false;
      if (this.#pending) {
        this.#pending = false;
        this.#reconcile();
      }
    }
  }

  /**
   * Resolves which tiers should currently have a gift in the cart.
   * @param {number} subtotal - Qualifying subtotal in Rappen (gifts excluded).
   * @param {Set<string>} presentHandles - Product handles in the cart (gifts excluded), lowercased.
   * @returns {Array<{ id: string, threshold: number, variantId?: number, handle?: string }>}
   */
  #desiredTiers(subtotal, presentHandles) {
    const tiers = this.#config?.tiers ?? [];
    const qualifying = tiers.filter((tier) => this.#tierQualifies(tier, subtotal, presentHandles));

    if (this.#config?.mode === 'highest') {
      if (qualifying.length === 0) return [];
      return [qualifying.reduce((best, tier) => (tier.threshold > best.threshold ? tier : best))];
    }

    return qualifying;
  }

  /**
   * A tier qualifies either by its product fence or by the subtotal threshold.
   * When a fence is set it takes over entirely: the tier qualifies only while one
   * of its fenced products is in the cart, and the threshold is ignored. Without
   * a fence, the classic subtotal-threshold rule applies.
   * @param {{ threshold: number, fence?: string[] }} tier
   * @param {number} subtotal - Qualifying subtotal in Rappen.
   * @param {Set<string>} presentHandles - Product handles in the cart, lowercased.
   * @returns {boolean}
   */
  #tierQualifies(tier, subtotal, presentHandles) {
    const fence = Array.isArray(tier.fence)
      ? tier.fence.filter(Boolean).map((handle) => String(handle).toLowerCase())
      : [];

    if (fence.length > 0) {
      return fence.some((handle) => presentHandles.has(handle));
    }

    return Number.isFinite(tier.threshold) && subtotal >= tier.threshold;
  }

  /**
   * Returns the gift tier id of a cart line, or null if it is not a gift line.
   * @param {{ properties?: Record<string, string> }} item
   * @returns {string | null}
   */
  #giftTierId(item) {
    const value = item?.properties?.[VF_AUTO_GIFT_PROPERTY];
    return value ? String(value) : null;
  }

  /**
   * @returns {Promise<any | null>}
   */
  async #getCart() {
    const response = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    return response.ok ? response.json() : null;
  }

  /**
   * @param {number} variantId
   * @param {{ id: string, label?: string }} tier
   * @returns {Promise<boolean>}
   */
  async #addGift(variantId, tier) {
    /** @type {Record<string, string>} */
    const properties = { [VF_AUTO_GIFT_PROPERTY]: tier.id };
    // Hidden (`_`-prefixed) label property so the cart badge can show the tier
    // text without it appearing in the normal line-item properties list.
    if (tier.label) properties._gift_label = tier.label;

    const body = JSON.stringify({
      items: [{ id: variantId, quantity: 1, properties }],
    });
    const response = await fetch(window.routes.cart_add_url, { ...fetchConfig(), body });
    return response.ok;
  }

  /**
   * @param {string} key - The cart line key.
   * @returns {Promise<boolean>}
   */
  async #removeLine(key) {
    const body = JSON.stringify({ id: key, quantity: 0 });
    const response = await fetch(window.routes.cart_change_url, { ...fetchConfig(), body });
    return response.ok;
  }

  /**
   * Repaints the header cart count, which does not listen for cart updates.
   * @returns {Promise<void>}
   */
  async #renderCartIconBubble() {
    const target = document.getElementById('cart-icon-bubble');
    if (!target) return;
    try {
      const response = await fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`);
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = html.querySelector('.shopify-section');
      if (source) target.innerHTML = source.innerHTML;
    } catch {
      // Stale badge is harmless; the next cart action repaints it.
    }
  }

  /**
   * Fallback variant lookup when a tier ships only a product handle.
   * @param {string | undefined} handle
   * @returns {Promise<number | null>}
   */
  async #resolveVariant(handle) {
    if (!handle) return null;
    try {
      const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
      const response = await fetch(`${root}/products/${handle}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      const product = await response.json();
      const variant = product.variants?.find((candidate) => candidate.available) || product.variants?.[0];
      return variant ? variant.id : null;
    } catch {
      return null;
    }
  }
}

if (!window.__vfAutoGiftInitialized) {
  window.__vfAutoGiftInitialized = true;
  const start = () => new VfAutoGift();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
}
